import React, { useContext } from 'react';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import _merge from 'lodash/merge';
import _isEmpty from 'lodash/isEmpty';
import { getContext } from 'common';

import Icon from 'components/Icon';
import { ChipContext } from './Chip';
import { ChipContext as InfoChipContext } from './InfoChip';

const SMALL_SIZE = '0.875rem';
const LARGE_SIZE = '1.5rem';

const ICON_CONFIG = ({
  small,
  color,
  infoChipStatus,
  avatar,
  RTL
}) => ({
  size: small ? { height: SMALL_SIZE, width: SMALL_SIZE } : { height: LARGE_SIZE, width: LARGE_SIZE },
  color,
  style: !avatar ? (RTL ? {
    marginRight: infoChipStatus && small ? `${Tokens.SPACING.SPACING_2}` : `${Tokens.SPACING.SPACING_4}`,
    marginLeft: `-${Tokens.SPACING.SPACING_4}`
  } : {
    marginLeft: infoChipStatus && small ? `${Tokens.SPACING.SPACING_2}` : `${Tokens.SPACING.SPACING_4}`,
    marginRight: `-${Tokens.SPACING.SPACING_4}`
  }) : {}
});

const GET_COLOR = (theme, { focused, hovered }) =>
  (focused && hovered) ? theme.hoveredFocused.icon
    : focused ? theme.focused.icon
      : hovered ? theme.hovered.icon : theme.icon;

export interface ChipIconProps extends React.ComponentPropsWithRef<'div'> {
  /** URL of Icon (alternatively you can also use the 'children' prop to pass in an SVG.) */
  src?: string;
  /** Indicates the Icon is within an avatar. Set to true, when placing <ChipIcon /> within <ChipAvatar /> */
  avatar?: boolean;
  /** To override the theme refer to the README tab. */
  theme?: any;
}

/** All props will be provided to `<Icon />` component, the size of the `ChipIcon` is read from `ChipContext`. */
const ChipIcon = React.forwardRef<HTMLElement, ChipIconProps>((props: ChipIconProps, ref) => getContext(({ RTL, theme }) => {
  const {
    theme: themeProp,
    avatar,
    ...otherProps
  } = props;
  const resolvedContext: any = _isEmpty(useContext(ChipContext)) ? InfoChipContext : ChipContext;
  const {
    focused,
    hovered,
    selected,
    error,
    size,
    infoChipStatus
  } = useContext(resolvedContext);
  const resolvedTheme = _merge({}, infoChipStatus ? theme.INFO_CHIP[infoChipStatus] : theme.CHIP, themeProp);
  const resolvedColor = infoChipStatus ? resolvedTheme.icon
    : selected ? GET_COLOR(resolvedTheme.selected, { focused, hovered })
      : error ? GET_COLOR(resolvedTheme.error, { focused, hovered })
        : GET_COLOR(resolvedTheme.default, { focused, hovered });

  const iconProps = {
    ...ICON_CONFIG({
      small: size === 'small',
      color: resolvedColor,
      infoChipStatus,
      RTL,
      avatar
    }),
    role: 'button',
    ...otherProps,
  };
  return <Icon {...iconProps} ref={ref} />;
}));

ChipIcon.displayName = 'ChipIcon';

ChipIcon.defaultProps = {
  src: undefined,
  theme: {},
  avatar: false
};

export default ChipIcon;